import React, { useEffect, useState, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../industry/monthPicker.css";
import { toast } from "react-toastify";

import AdminLayout from "../../layout/admin";
import { getMethodApiCall, postMethodApiCall } from "../../services/api";
import GenericDataLayout from "../../components/custom/GenericDataLayout";
import GenericTable from "../../components/custom/GenericTable";

const ApproveAttendance = () => {
  const [loading, setLoading] = useState(false);
  const [actionRowId, setActionRowId] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ industries: [], trades: [] });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [selectedTrade, setSelectedTrade] = useState("all");

  const selectedYear = String(selectedDate.getFullYear());
  const selectedMonth = String(selectedDate.getMonth() + 1);

  const user = JSON.parse(localStorage.getItem("loginedUser"));
  const itiId = user?.iti?.id;
  const itiName = user?.iti?.iti_name || "";

  const fetchApprovedAttendance = useCallback(async () => {
    if (!itiId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        year: selectedYear,
        month: selectedMonth,
      });
      if (selectedIndustry !== "all") params.set("industry_id", selectedIndustry);
      if (selectedTrade !== "all") params.set("trade_id", selectedTrade);

      const url = `/v1/api/iti/approved-attendance/${itiId}?${params.toString()}`;
      const res = await getMethodApiCall(url);
      if (res?.status === 200) {
        setRows(res.data?.data || []);
        setFilterOptions(res.data?.filterOptions || { industries: [], trades: [] });
        setTotalRecords(res.data?.meta?.totalRecords || 0);
        setTotalPages(res.data?.meta?.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching ITI approved attendance:", error);
    } finally {
      setLoading(false);
    }
  }, [itiId, page, limit, selectedYear, selectedMonth, selectedIndustry, selectedTrade]);

  useEffect(() => {
    fetchApprovedAttendance();
  }, [fetchApprovedAttendance]);

  const handleClearFilters = () => {
    setPage(1);
    setLimit(10);
    setSelectedDate(new Date());
    setSelectedIndustry("all");
    setSelectedTrade("all");
  };

  const handleApprovalDecision = async (row, approved) => {
    if (!itiId || !row) return;
    setActionRowId(row.id);
    try {
      const url = `/v1/api/iti/approved-attendance/${itiId}/approve`;
      const res = await postMethodApiCall(url, {
        year: row.year,
        month: row.month,
        industry_id: row.industry_id,
        trade_id: row.trade_id,
        approved,
      });
      if (res?.status === 200) {
        toast.success(approved ? "Attendance approved successfully" : "Attendance rejected successfully");
        await fetchApprovedAttendance();
      }
    } catch (error) {
      console.error("Error updating attendance approval:", error);
      toast.error(
        error?.response?.data?.message ||
          (approved ? "Failed to approve attendance" : "Failed to reject attendance")
      );
    } finally {
      setActionRowId(null);
    }
  };

  const industryOptions = (filterOptions?.industries || []).map((industry) => ({
    label: industry.name,
    value: String(industry.id),
  }));

  const tradeOptions = (filterOptions?.trades || []).map((trade) => ({
    label: trade.name,
    value: String(trade.id),
  }));

  const filtersConfig = [
    {
      type: "custom",
      render: () => (
        <div className="w-full">
          <label className="text-xs font-semibold text-primary mb-1.5 block">Month & Year</label>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              setPage(1);
              setSelectedIndustry("all");
              setSelectedTrade("all");
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            maxDate={new Date()}
            onKeyDown={(e) => e.preventDefault()}
            wrapperClassName="w-full"
            className="w-full text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-primary/30 focus:border-primary outline-none transition-all bg-white hover:border-gray-300 h-[36px] px-3 py-1.5 text-gray-700 cursor-pointer caret-transparent"
          />
        </div>
      ),
    },
    {
      type: "select",
      label: "Industry",
      value: selectedIndustry,
      options: industryOptions,
      onChange: (val) => {
        setSelectedIndustry(val);
        setPage(1);
      },
    },
    {
      type: "select",
      label: "Trade",
      value: selectedTrade,
      options: tradeOptions,
      onChange: (val) => {
        setSelectedTrade(val);
        setPage(1);
      },
    },
  ];

  const paginationConfig = {
    page,
    limit,
    totalRecords,
    totalPages,
    onPageChange: (newPage) => setPage(newPage),
    onLimitChange: (newLimit) => {
      setLimit(newLimit);
      setPage(1);
    },
  };

  const columns = [
    {
      header: "Month",
      render: (row) => row?.monthLabel || "-",
    },
    {
      header: "Industry",
      render: (row) => row?.industry_name || "-",
    },
    {
      header: "Trade",
      render: (row) => row?.trade_name || "-",
    },
    {
      header: "Doc",
      cellClassName: "whitespace-nowrap",
      render: (row) =>
        row?.s3_url ? (
          <button
            type="button"
            onClick={() => window.open(row.s3_url, "_blank", "noopener,noreferrer")}
            className="text-sm font-semibold text-primary hover:underline text-left"
          >
            Submitted Document
          </button>
        ) : (
          <span className="text-sm text-gray-400">Pending</span>
        ),
    },
    {
      header: "Approval",
      render: (row) => {
        const status = row.iti_status || row.approved_by_iti;

        if (status === "approved") {
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-700">
              Approved
            </span>
          );
        }

        if (status === "rejected") {
          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-700">
              Rejected
            </span>
          );
        }

        if (!row?.s3_url) {
          return <span className="text-sm text-gray-400">Pending</span>;
        }

        const isBusy = actionRowId === row.id;
        const btnBase =
          "box-border w-24 min-w-24 h-8 shrink-0 inline-flex items-center justify-center text-xs font-semibold rounded-md border transition-colors disabled:opacity-60";

        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <button
              type="button"
              disabled={isBusy}
              onClick={() => handleApprovalDecision(row, true)}
              className={`${btnBase} border-primary bg-primary text-white hover:bg-primary/90`}
            >
              Approve
            </button>
            <button
              type="button"
              disabled={isBusy}
              onClick={() => handleApprovalDecision(row, false)}
              className={`${btnBase} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
            >
              Reject
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Approve Attendance"
        subtitle={itiName ? `${itiName} ITI` : ""}
        filters={filtersConfig}
        onResetFilters={handleClearFilters}
        pagination={paginationConfig}
        isLoading={loading}
        actions={[]}
      >
        <GenericTable
          columns={columns}
          data={rows}
          pagination={paginationConfig}
        />
      </GenericDataLayout>
    </AdminLayout>
  );
};

export default ApproveAttendance;
